import React from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { MdAdd, MdDelete } from 'react-icons/md';
import { FiChevronDown } from 'react-icons/fi';
import { ComplexValue, FavouriteReplaceDomainNodes } from 'app/types-shared';
import { AddButton, ButtonTertiary, ElementLoader, LoadOptions, ValidationProps } from 'ui';
import {
  getAllOptionIds,
  getFilteredDomains,
  getReplaceDomainNodes,
  isAddReplaceDomainNodeDisabled,
  isReadOnlyReplaceDomainNode
} from 'app/components/filters/filter-replacements/get-replace-domain-nodes';
import { FiltersTranslation } from 'app/area/filters/constants';
import { MapFilterType, ReplaceDomainNodeType } from 'app/components/filters/filter-replacements/constants';
import { SelectSingleHierarchy } from 'app/area/shared/translated-components';
import { FilterReplacementsInfo } from 'app/components/filters/filter-replacements/filter-replacement-info';
import { ModalSize } from 'app/components/constants';
import { createExampleInfoText } from 'app/components/filters/filter-replacements/create-example-info-text';
import styles from './section-filters.scss';

export interface FilterReplacementsMapFiltersProps {
  replaceDomainNodes: FavouriteReplaceDomainNodes[];
  removeDomainNodeOptions: ComplexValue<number>[];
  originalDomainNodeOptions?: ComplexValue<number>[];
  replacementDomainNodeOptions: ComplexValue<number>[];
  fetchingExtendedReplaceDomainNodes?: boolean;
  validation: ValidationProps[];
  translation: FiltersTranslation;
  locale: string;
  onFieldChange: (index: number, type: MapFilterType) => (updatedNode: ComplexValue<number>) => void;
  removeReplaceDomainNode: (index: number) => void;
  onLoadOriginalDomainNodesFields: ({ optionsIds, query }: { optionsIds?: number[]; query?: string }) => void;
  onLoadReplaceDomainNodesFields: ({ domain, query }: { domain?: number; query?: string }) => void;
  onLoadRemoveNodesFields: ({ optionsIds, query }: { optionsIds?: number[]; query?: string }) => void;
  updateReplaceDomainNodes: (replaceDomainNodes: FavouriteReplaceDomainNodes[]) => void;
}

export interface FilterReplacementsMapFiltersState {
  loadOptions: LoadOptions;
  isFetching: boolean;
  isExpanded: boolean;
}

export class FilterReplacementsMapFilters extends React.Component<FilterReplacementsMapFiltersProps,
  FilterReplacementsMapFiltersState> {

  public state: Readonly<FilterReplacementsMapFiltersState> = {
    loadOptions: undefined,
    isFetching: false,
    isExpanded: true
  };

  public componentDidUpdate(prevProps: FilterReplacementsMapFiltersProps) {
    if (this.props.removeDomainNodeOptions !== prevProps.removeDomainNodeOptions) {
      this.setState({ isFetching: false });
    }
  }

  private onLoadOriginalOptions = (loadOptions: LoadOptions) => {
    const optionsIds = getAllOptionIds(this.props.replaceDomainNodes);
    this.setState({ loadOptions, isFetching: true }, () => {
      this.props.onLoadOriginalDomainNodesFields({ optionsIds, query: loadOptions.query });
    });
  };

  private onLoadReplacementOptions = (domain: number) => {
    return (loadOptions: LoadOptions) => {
      this.setState({ loadOptions, isFetching: true }, () => {
        this.props.onLoadReplaceDomainNodesFields({ domain, query: loadOptions.query });
      });
    };
  };

  private onToggle = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    this.setState({ isExpanded: !this.state.isExpanded });
  };

  private addReplaceDomainNode = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();

    this.props.updateReplaceDomainNodes([
      ...this.props.replaceDomainNodes, {
        domainNode: { v: 0 },
        replaceDomainNode: { v: 0 }
      }
    ]);
  };

  private getExtendedDomainNode = (domainNode) => {
    return getFilteredDomains(domainNode).map((desc, descIndex) => (
        <span key={descIndex}>{desc?.pad}</span>
      ));
  };

  public render() {

    const defaultReplaceDomainNodes = getReplaceDomainNodes(ReplaceDomainNodeType.Default, this.props.replaceDomainNodes);

    return <>
      <div className={styles.filterReplacementWrapper}>
        <div className={styles.filterReplacementContent}>
          <h3 className="align-items-center">
            {`${this.props.translation.filter_replacements_map_filters_section_title} 
            ${defaultReplaceDomainNodes.length > 0 ? `(${defaultReplaceDomainNodes.length})` : ''}`}
          </h3>
          <div className={styles.toggleExpand}>
            <ButtonTertiary onClick={this.onToggle}>
              {this.state.isExpanded ?
                this.props.translation.filter_replacements_hide_details :
                this.props.translation.filter_replacements_show_details}
              <FiChevronDown className={this.state.isExpanded ? 'expand-icon-down' : 'expand-icon-up'} />
            </ButtonTertiary>
          </div>
        </div>
        <div className={this.state.isExpanded ? 'mt-3' : 'd-none'}>
          <p>{this.props.translation.filter_replacements_map_filters_description}</p>

          <FilterReplacementsInfo
            size={ModalSize.large}
            titleText={this.props.translation.filter_replacements_map_filters_section_title}
            infoText={<div className="row">
              <div className="col-sm-12 col-md-4">
                {createExampleInfoText(
                  this.props.translation.filter_replacements_map_example_first,
                  <span className={styles.exampleTitle}>
                    {this.props.translation.filter_replacements_map_filters_title_first}
                  </span>,
                  <span className={styles.exampleHighlightedText}>
                    {this.props.translation.filter_replacements_where_and_what}
                  </span>,
                  <span className={styles.exampleHighlightedText}>
                    {this.props.translation.filter_replacements_map_filters_employed_unit}
                  </span>
                )}
              </div>
              <div className="col-sm-12 col-md-4">
                {createExampleInfoText(
                  this.props.translation.filter_replacements_map_example_second,
                  <span className={styles.exampleTitle}>
                    {this.props.translation.filter_replacements_map_filters_title_second}
                  </span>,
                  <span className={styles.exampleHighlightedText}>
                    {this.props.translation.filter_replacements_map_filters_date_time}
                  </span>,
                  <span className={styles.exampleHighlightedText}>
                    {this.props.translation.filter_replacements_map_filters_man_hours}</span>,
                  <span className={styles.exampleHighlightedText}>
                    {this.props.translation.filter_replacements_map_filters_period}
                  </span>
                )}
              </div>
              <div className="col-sm-12 col-md-4">
                {createExampleInfoText(
                  this.props.translation.filter_replacements_map_example_third,
                  <span className={styles.exampleTitle}>{this.props.translation.filter_replacements_map_filters_title_third}</span>,
                  <span className={styles.exampleHighlightedText}>
                    {this.props.translation.filter_replacements_map_filters_case_no}
                  </span>,
                  <span className={styles.exampleHighlightedText}>
                    {this.props.translation.filter_replacements_map_filters_superior}
                  </span>
                )}
              </div>
            </div>}
            translation={this.props.translation} />
          <div className={styles.listItemsContainer}>
            <div className={styles.listItems}>
              {this.props.fetchingExtendedReplaceDomainNodes
                ? <ElementLoader />
                : <div>
                  <div className={styles.headingContainer}>
                    <div className="row">
                      <div className="col-lg-6 col-sm-12">
                        {this.props.translation.filter_replacements_map_filters_original_field}
                      </div>
                      <div className="col-lg-6 col-sm-12">
                        {this.props.translation.filter_replacements_map_filters_replacement_field}
                      </div>
                    </div>
                  </div>
                  {defaultReplaceDomainNodes?.length > 0 ? (
                    defaultReplaceDomainNodes.map((node, index) => (
                      <div className='position-relative' key={index}>
                        <div className={`${styles.listItemRow} ${index % 2 === 0 ? styles.zebra : ''}`}>
                          <div className={styles.content}>
                            <div className="row align-items-center">
                              <div className="col-lg-5 col-sm-12">
                                {!node.domainNode.v
                                  ? <SelectSingleHierarchy
                                    componentId={`${MapFilterType.DomainNode}-${index}`}
                                    className={styles.selectMapFilter}
                                    label={{ text: this.props.translation.filter_replacements_map_filters_original_field }}
                                    placeholder={this.props.translation?.filters_search_placeholder}
                                    valueItem={{
                                      value: node.domainNode.v,
                                      description: node.domainNode.d
                                    }}
                                    validation={this.props.validation.find(node => node?.messages?.[0]?.parameters?.domainNode === index)}
                                    readOnly={isReadOnlyReplaceDomainNode(node)}
                                    loadOptions={this.state.loadOptions}
                                    options={this.props.originalDomainNodeOptions}
                                    onChange={this.props.onFieldChange(index, MapFilterType.DomainNode)}
                                    onLoadOptions={this.onLoadOriginalOptions}
                                    fetching={this.state.isFetching}
                                    alwaysOpen={false}
                                    leafNodeOnly={true}
                                    initiallyShowLongDescription={true}
                                    longDescription={true}
                                    descriptionAllLevels={true}
                                    locale={this.props.locale} />
                                  : (!!node?.domainNode.domains?.length ? <div className={styles.originalFieldValue}>
                                    <p>{node.domainNode.d}</p>
                                    {this.getExtendedDomainNode(node.domainNode)}
                                  </div> : <div className={styles.originalFieldValue}><p>{node.domainNode.d}</p></div>)}
                              </div>
                              <div className={`col-lg-1 col-sm-12 text-center ${styles.arrowColumn}`}>
                                <FaArrowRight />
                              </div>
                              <div className="col-lg-5 col-sm-12">
                                <SelectSingleHierarchy
                                  componentId={`replace-domain-node-${index}`}
                                  className={styles.selectMapFilter}
                                  label={{ text: this.props.translation.filter_replacements_map_filters_replacement_field }}
                                  placeholder={this.props.translation?.filters_search_placeholder}
                                  valueItem={{
                                    value: node.replaceDomainNode.v,
                                    description: node.replaceDomainNode?.domain?.ald ? node.replaceDomainNode.domain.ald : node.replaceDomainNode.d
                                  }}
                                  validation={this.props.validation.find(node => node?.messages?.[0]?.parameters?.replaceDomainNode === index)}
                                  loadOptions={this.state.loadOptions}
                                  options={this.props.replacementDomainNodeOptions}
                                  readOnly={!isReadOnlyReplaceDomainNode(node)}
                                  onChange={this.props.onFieldChange(index, MapFilterType.ReplaceDomainNode)}
                                  onLoadOptions={this.onLoadReplacementOptions(node.domainNode.v)}
                                  fetching={this.state.isFetching}
                                  leafNodeOnly={true}
                                  descriptionAllLevels={true}
                                  locale={this.props.locale} />
                              </div>
                              <div className={`col-lg-1 col-sm-12 text-center ${styles.arrowColumn}`}>
                                <button type="button" className={styles.deleteButton} onClick={() => this.props.removeReplaceDomainNode(index)}>
                                  <MdDelete />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className={styles.noMapFiltersDefined}>
                      {this.props.translation.filter_replacements_no_map_filters_defined}
                    </p>
                  )}
                </div>}
            </div>
            <div className={styles.addButton}>
              <AddButton
                disabled={isAddReplaceDomainNodeDisabled(this.props.replaceDomainNodes) || this.props.fetchingExtendedReplaceDomainNodes}
                icon={MdAdd}
                onClick={this.addReplaceDomainNode}>{this.props.translation.filter_replacements_add}</AddButton>
            </div>
          </div>
        </div>
      </div>
    </>;
  }
}
